import React, { useState, useEffect } from 'react';
import { Flame, Trophy, Star } from 'lucide-react';
import { User } from '../types';
import { calculateLevel, xpToNextLevel } from '../config/gamificationConfig';
import { subscribeToGamification, getGamificationSummary } from '../services/gamificationService';

interface UserStatusWidgetProps {
    user: User;
    xp: number;
    streak: number;
    onClick: () => void;
    isDarkMode: boolean;
}

const UserStatusWidget: React.FC<UserStatusWidgetProps> = ({ user, xp: initialXP, streak: initialStreak, onClick, isDarkMode }) => {
    // Use local state to track real-time gamification updates
    // Initialize with props first, then immediately sync with service
    const [stats, setStats] = useState({
        xp: initialXP,
        streak: initialStreak
    });

    useEffect(() => {
        // Subscribe to gamification updates
        const unsubscribe = subscribeToGamification((state) => {
            setStats({
                xp: state.totalXP,
                streak: state.currentStreak
            });
        });

        // Also fetch latest summary immediately to ensure we're in sync
        const summary = getGamificationSummary();
        setStats({
            xp: summary.xp.total,
            streak: summary.streak.current
        });

        return () => unsubscribe();
    }, []);

    const levelInfo = calculateLevel(stats.xp);
    const progressInfo = xpToNextLevel(stats.xp);

    return (
        <button
            onClick={onClick}
            className={`
                group relative flex items-center gap-3 pl-3 pr-4 py-1.5 rounded-xl transition-all duration-200
                ${isDarkMode
                    ? 'bg-slate-800/50 hover:bg-slate-800'
                    : 'bg-white hover:bg-slate-50 shadow-sm hover:shadow'}
            `}
        >
            {/* Level Badge */}
            <div className="relative">
                <div className={`
                    w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm shadow-sm
                    bg-indigo-600 text-white
                `}>
                    {levelInfo.level}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center">
                    <Star size={8} className="text-amber-900 fill-current" />
                </div>
            </div>

            {/* Stats */}
            <div className="flex flex-col items-start">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {levelInfo.title}
                    </span>
                    {stats.streak > 0 && (
                        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-500 text-[10px] font-bold">
                            <Flame size={10} className="fill-current" />
                            {stats.streak}
                        </div>
                    )}
                </div>

                {/* XP Progress Bar */}
                <div className="w-24 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${progressInfo.progress}%` }}
                    />
                </div>
            </div>
        </button>
    );
};

export default UserStatusWidget;
